import generateOccupancyStringByRoom from 'lib/offer/generateOccupancyStringByRoom'
import request from 'api/requestUtils'
import { templates } from '@luxuryescapes/lib-uri-templates'
import { serialiseOccupancy } from 'lib/search/occupancy'
import qs from 'qs'
import bedbankRateMap from './mappers/bedbankRateMap'
import { bedBankOfferMap, bedbankPackageMap } from './mappers/bedbankOfferMap'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import type { PublicOfferV2 } from '@luxuryescapes/contract-public-offer'
import wait from 'lib/promise/wait'
import { arrayToObject } from 'lib/array/arrayUtils'

interface getPackagesByBedbankOfferIdParams {
  checkIn: string
  checkOut: string
  region: string
  id: string
  rooms: Array<App.Occupants>
  timezone?: string
  preview?: string
  isSpoofed?: boolean
  source?: string
  markupSource?: ChannelMarkupSource
}

export function getRatesForBedbankOffer(params: getPackagesByBedbankOfferIdParams) {
  const uri = templates.offer.publicOfferPackages.expand({
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    occupancy: params.rooms.map(generateOccupancyStringByRoom),
    offerId: params.id,
    region: params.region,
    preview: params.preview,
    source: params.source,
  })

  return request.get<App.ApiResponse<Array<PublicOfferV2.BedbankPackage>>>(uri, params.isSpoofed ? { credentials: 'include' } : {}).then(data => data.result.flatMap(pkg => {
    const room = bedbankPackageMap(pkg, params.id, params.markupSource)
    return (pkg.rates ?? []).map(rate => bedbankRateMap({
      checkIn: params.checkIn,
      checkOut: params.checkOut,
      timezone: params.timezone,
      rate,
      room,
      offerId: params.id,
      markupSource: params.markupSource,
    }))
  }))
}

interface getPackagesByBedbankOffersParams {
  checkIn: string
  checkOut: string
  region: string
  rooms: Array<App.Occupants>
  preview?: string
  isSpoofed?: boolean
  source?: string
  markupSource?: ChannelMarkupSource
}

function getRatesForBedbankOffersQueryString(offerIds: Array<string>, params: getPackagesByBedbankOffersParams, enablePropertyCollect: boolean) {
  const defaultQueryObject = {
    offerIds: offerIds.join(','),
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    region: params.region,
    preview: params.preview,
    occupancy: params.rooms ? serialiseOccupancy(params.rooms) : undefined,
    source: params.source,
  }

  if (enablePropertyCollect) {
    return qs.stringify({
      ...defaultQueryObject,
      enablePropertyCollect,
    })
  }

  return qs.stringify(defaultQueryObject)
}

export function getRatesForBedbankOffers(offerIds: Array<string>, params: getPackagesByBedbankOffersParams, enablePropertyCollect: boolean) {
  const uri = templates.offer.publicOffers.expand({})

  const queryStrings = getRatesForBedbankOffersQueryString(offerIds, params, enablePropertyCollect)

  return request.get<App.ApiResponse<Array<PublicOfferV2.BedbankOffer>>>(`${uri}?${queryStrings}`, params.isSpoofed ? { credentials: 'include' } : {})
    .then((response) =>
      response.result.map(serverOffer => {
        return {
          offerId: serverOffer.id,
          rates: serverOffer.packages.flatMap(pkg => {
            const mappedPackage = bedbankPackageMap(pkg, serverOffer.id, params.markupSource)
            return (pkg.rates ?? []).map(rate => bedbankRateMap({
              checkIn: params.checkIn,
              checkOut: params.checkOut,
              timezone: serverOffer.property.timezone,
              rate,
              room: mappedPackage,
              offerId: serverOffer.id,
              markupSource: params.markupSource,
            }))
          }),
        }
      }),
    )
}

interface GetBedbankOfferWithRatesParams {
  checkIn?: string
  checkOut?: string
  region: string
  rooms?: Array<App.Occupants>
  preview?: string
  isSpoofed?: boolean
  source?: string
  enablePropertyCollect?: boolean
  markupSource?: ChannelMarkupSource
}

/**
 * Fetches one bedbank offer and its rates from `/api/v2/public-offers`.
 * Returns mapped offer data and rate rows for state updates.
 */
export function getBedbankOfferWithRates(
  offerId: string,
  params: GetBedbankOfferWithRatesParams,
): Promise<{ offer: App.BedbankOffer, rates: Array<App.BedbankRate> }> {
  const uri = templates.offer.publicOffers.expand({})

  const query = qs.stringify({
    offerIds: offerId,
    checkIn: params.checkIn,
    checkOut: params.checkOut,
    region: params.region,
    occupancy: params.rooms ? serialiseOccupancy(params.rooms) : undefined,
    preview: params.preview,
    source: params.source,
    ...(params.enablePropertyCollect ? { enablePropertyCollect: params.enablePropertyCollect } : {}),
  })

  return request
    .get<App.ApiResponse<Array<PublicOfferV2.BedbankOffer>>>(
      `${uri}?${query}`,
      params.isSpoofed ? { credentials: 'include' } : {},
    )
    .then((response) => {
      const serverOffer = response.result[0]
      if (!serverOffer) {
        return Promise.reject({ status: 404, message: `Offer not found ${offerId}` })
      }

      const offer = bedBankOfferMap(serverOffer, params.source, params.markupSource)

      const rates: Array<App.BedbankRate> = (params.checkIn && params.checkOut)
        ? serverOffer.packages.flatMap((pkg) => {
            const room = bedbankPackageMap(pkg, serverOffer.id, params.markupSource)
            return (pkg.rates ?? []).map((rate) => bedbankRateMap({
              checkIn: params.checkIn!,
              checkOut: params.checkOut!,
              timezone: serverOffer.property.timezone,
              rate,
              room,
              offerId: serverOffer.id,
              markupSource: params.markupSource,
            }))
          })
        : []

      return { offer, rates }
    })
}

const ratesFetchAccumulator: Record<string, Array<string>> = {}
const ratesFetchPromise: Record<string, Promise<{ [id: string]: { offerId: string, rates: Array<App.BedbankRate> } }> | undefined> = {}

/**
 * This function will get and return a bedbank rate
 * However, it won't always do this straight away or in a separate request
 * The function will 'accumulate' all requests over a period of time and then fetch them
 * all in one go - returning them individually to the appropriate function call
 * @returns A promise with the bedbank rates requested
 */
export function getAccumulatedBedbankRatesById(params: getPackagesByBedbankOfferIdParams, enablePropertyCollect: boolean) {
  const paramsKey = `${params.checkIn}-${params.checkOut}-${serialiseOccupancy(params.rooms ?? [])}`
  if (!ratesFetchAccumulator[paramsKey]) {
    ratesFetchAccumulator[paramsKey] = []
  }
  ratesFetchAccumulator[paramsKey].push(params.id)
  let promise = ratesFetchPromise[paramsKey]
  if (!promise) {
    // give it 32ms to accumulate offer calls - that's around two frames at 60 fps worth of time
    promise = wait(32).then(async() => {
      const offerIdsToFetch = ratesFetchAccumulator[paramsKey]
      // reset our state in prep for next set of calls
      ratesFetchAccumulator[paramsKey] = []
      ratesFetchPromise[paramsKey] = undefined

      const rates = await getRatesForBedbankOffers(offerIdsToFetch, params, enablePropertyCollect)
      const ratesById = arrayToObject(rates, rates => rates.offerId)
      return ratesById
    })
    ratesFetchPromise[paramsKey] = promise
  }

  return promise.then((rates) => rates[params.id].rates)
}
